import { Combobox, useCombobox, TextInput, ScrollArea } from '@mantine/core';
import { ReactNode, CSSProperties } from 'react';

import classes from './EditableSelect.module.scss';
import { SelectOption } from '../SelectOption';
import { selectOptionOnceMounted } from '../utils/selectOptionOnceMounted';

type EditableSelectProps = {
  data: string[];
  value: string;
  setValue: (newValue: string) => void;
  type?: string;
  leftSection?: ReactNode;
  leftSectionWidth?: number;
  title?: string;
  inputClassNames?: Record<string, string>;
  testId?: string;
  style?: CSSProperties;
};

export function EditableSelect(props: EditableSelectProps) {
  const {
    data,
    value,
    setValue,
    type = 'text',
    leftSection,
    leftSectionWidth,
    title,
    inputClassNames,
    testId,
    style
  } = props;
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => selectOptionOnceMounted(combobox, data.indexOf(value))
  });

  const options = data.map(item => (
    <Combobox.Option key={item} value={item}>
      <SelectOption checked={item === value}>{item}</SelectOption>
    </Combobox.Option>
  ));

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.currentTarget.value);
    combobox.openDropdown();
    combobox.updateSelectedOptionIndex();
  };

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={val => {
        setValue(val);
        combobox.closeDropdown();
      }}
      withinPortal={false}
      // Mantine keeps floating-ui observing a mounted dropdown's target whether it is open or not, and a
      // Combobox keeps its dropdown mounted by default. This select sits in rich-text toolbars over the
      // stage, where observers inside a resize frame loop (documentation/resize-observers.md, rule 4).
      keepMounted={false}
      classNames={{
        option: classes.optionWrapper,
        dropdown: classes.dropdown
      }}
      data-testid={testId}
    >
      <Combobox.Target>
        <TextInput
          type={type}
          value={value}
          onChange={handleInputChange}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          leftSection={leftSection}
          leftSectionWidth={leftSectionWidth}
          classNames={inputClassNames}
          title={title}
          rightSection={<></>}
          rightSectionWidth={'0px'}
          style={style}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea.Autosize type="scroll" mah={210}>
            {options}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
